import React from 'react';
import LandingLayout from '../layouts/LandingLayout';
import { Calendar, Clock, Globe, Users, Zap, Shield, Bell, Palette } from 'lucide-react';
import { motion } from 'framer-motion';

const features = [
  {
    icon: <Calendar className="h-6 w-6" />,
    title: 'Smart Scheduling',
    description: 'Intelligent calendar management that automatically detects and suggests the best meeting times.',
  },
  {
    icon: <Clock className="h-6 w-6" />,
    title: 'Time Zone Intelligence',
    description: 'Seamless scheduling across time zones with automatic detection and conversion.',
  },
  {
    icon: <Users className="h-6 w-6" />,
    title: 'Group Meetings',
    description: 'Coordinate multiple attendees with ease using our smart group scheduling system.',
  },
  {
    icon: <Zap className="h-6 w-6" />,
    title: 'Instant Booking',
    description: 'One-click booking process with immediate confirmation and calendar updates.',
  },
  {
    icon: <Globe className="h-6 w-6" />,
    title: 'Custom Availability',
    description: 'Set your availability preferences and let others book according to your schedule.',
  },
  {
    icon: <Shield className="h-6 w-6" />,
    title: 'Secure & Private',
    description: 'Enterprise-grade security with end-to-end encryption for all your scheduling data.',
  },
  {
    icon: <Bell className="h-6 w-6" />,
    title: 'Smart Notifications',
    description: 'Automated reminders and updates to keep everyone in sync and on time.',
  },
  {
    icon: <Palette className="h-6 w-6" />,
    title: 'Customizable',
    description: 'Personalize your booking page with custom branding and scheduling rules.',
  },
];

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
};

const Features: React.FC = () => {
  return (
    <LandingLayout>
      <div className="min-h-screen bg-gradient-to-b from-background to-background/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center mb-20">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/70">
              Powerful Features
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Everything you need to streamline your scheduling process and boost your productivity
            </p>
          </div>

          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={item}
                className="p-6 rounded-2xl bg-card border border-border/50 hover:border-primary/50 transition-colors duration-300"
              >
                <div className="flex items-center space-x-4 mb-4">
                  <div className="p-3 rounded-xl bg-primary/10 text-primary">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold">{feature.title}</h3>
                </div>
                <p className="text-muted-foreground">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>

          <div className="mt-24 text-center">
            <div className="inline-flex items-center space-x-2 text-sm text-muted-foreground bg-muted px-4 py-2 rounded-full">
              <Zap className="h-4 w-4" />
              <span>More features coming soon</span>
            </div>
          </div>
        </div>
      </div>
    </LandingLayout>
  );
};

export default Features; 